import {
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons"
import { Tooltip } from "antd"
import { useMemo } from "react"

import Flex from "#blocks/Flex"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { type AttendanceStatus } from "#customTypes/types"
import { COLORS } from "#styles/colors"
import { camelToWords } from "#utils/textFormatters"

const ICON_SIZE = {
  small: 10,
  medium: 16,
  large: 20,
}

const AttendanceIcon = ({
  status,
  size = "medium",
}: {
  status: AttendanceStatus
  size?: "small" | "medium" | "large"
}) => {
  const { attendance } = useThemeContext()

  const ATTENDANCE_STATUS_BUTTONS = useMemo(
    () => ({
      [ATTENDANCE_STATUSES.ABSENT]: {
        icon: <CloseOutlined style={{ fontSize: ICON_SIZE[size] }} />,
        color: "white",
      },
      [ATTENDANCE_STATUSES.PRESENT]: {
        icon: <CheckOutlined style={{ fontSize: ICON_SIZE[size] }} />,
        color: "white",
      },
      [ATTENDANCE_STATUSES.TARDY]: {
        icon: (
          <ClockCircleOutlined
            style={{ fontSize: Math.round(ICON_SIZE[size]) * 1.15 }}
          />
        ),
        color: "white",
      },

      [ATTENDANCE_STATUSES.PLANNED_ABSENCE]: {
        icon: <CloseOutlined style={{ fontSize: ICON_SIZE[size] }} />,
        color: "white",
      },

      [ATTENDANCE_STATUSES.MAKEUP_PRESENT]: {
        icon: <CheckOutlined style={{ fontSize: ICON_SIZE[size] }} />,
        color: "white",
      },
      [ATTENDANCE_STATUSES.MAKEUP_TARDY]: {
        icon: (
          <ClockCircleOutlined
            style={{ fontSize: Math.round(ICON_SIZE[size] * 1.1) }}
          />
        ),
        color: "white",
      },
      [ATTENDANCE_STATUSES.MAKEUP_ABSENT]: {
        icon: <CloseOutlined style={{ fontSize: ICON_SIZE[size] }} />,
        color: "white",
      },
      [ATTENDANCE_STATUSES.MAKEUP_ENROLLED]: {
        icon: (
          <QuestionCircleOutlined
            style={{ fontSize: Math.round(ICON_SIZE[size] * 1.1) }}
          />
        ),
        color: "white",
      },

      [ATTENDANCE_STATUSES.TRYOUT_PRESENT]: {
        icon: <CheckOutlined style={{ fontSize: ICON_SIZE[size] }} />,
        color: "white",
      },
      [ATTENDANCE_STATUSES.TRYOUT_TARDY]: {
        icon: (
          <ClockCircleOutlined
            style={{ fontSize: Math.round(ICON_SIZE[size] * 1.1) }}
          />
        ),
        color: "white",
      },
      [ATTENDANCE_STATUSES.TRYOUT_ABSENT]: {
        icon: <CloseOutlined style={{ fontSize: ICON_SIZE[size] }} />,
        color: "white",
      },
      [ATTENDANCE_STATUSES.TRYOUT_ENROLLED]: {
        icon: (
          <QuestionCircleOutlined
            style={{ fontSize: Math.round(ICON_SIZE[size] * 1.1) }}
          />
        ),
        color: "white",
      },

      // Need to figure out a way around these statuses
      [ATTENDANCE_STATUSES.NOT_ENROLLED]: {
        icon: (
          <span
            style={{
              height: ICON_SIZE[size],
              width: ICON_SIZE[size],
              backgroundColor: "transparent",
            }}
          ></span>
        ),
        color: COLORS.TRANSPARENT,
      },
      [ATTENDANCE_STATUSES.ENROLLED]: {
        icon: (
          <QuestionCircleOutlined style={{ fontSize: ICON_SIZE[size] * 1.1 }} />
        ),
        color: "white",
      },
      [ATTENDANCE_STATUSES.INVALID]: {
        icon: <ExclamationCircleOutlined />,
        color: COLORS.error,
        background: COLORS.GRAY1,
        tooltip: `Invalid status: ${status}`,
      },
    }),
    [size, status]
  )

  const { icon, color, background, tooltip } = useMemo(() => {
    const isStatusValid = !!ATTENDANCE_STATUSES[status]
    const statusKey = isStatusValid ? status : ATTENDANCE_STATUSES.INVALID

    return {
      icon: ATTENDANCE_STATUS_BUTTONS[statusKey].icon,
      color: ATTENDANCE_STATUS_BUTTONS[statusKey].color,
      background: attendance[statusKey],
      tooltip: isStatusValid ? camelToWords(statusKey) : "Invalid status",
    }
  }, [status, attendance, ATTENDANCE_STATUS_BUTTONS])

  return (
    <Flex
      center
      style={{
        borderRadius: "50%",
        background:
          !background || background === "transparent"
            ? "transparent"
            : background + "99", // 60% opacity
        border: `1px solid ${background}`,
        padding: 3,
        color: color,
      }}
    >
      <Tooltip
        title={tooltip}
        // arrow={false}
        mouseEnterDelay={0.5}
        mouseLeaveDelay={0}
        // placement="bottom"
      >
        {icon}
      </Tooltip>
    </Flex>
  )
}

export default AttendanceIcon
